/**
 * AllResults
 *
 * Aggregates and renders grouped result sections for People, Objectives, Key Results,
 * Initiatives, and Milestones. Delegates the actual rendering to their dedicated
 * sub-components and only shows sections that have at least one item.
 *
 * Props
 * - peopleData: Person[]
 * - objectivesData: Objective[]
 * - initiativesData: Initiative[]
 * - keyResultsData: KeyResult[]
 * - milestonesData: Milestone[]
 * - isAllTab?: Optional flag; present for parent context (no behavior change here)
 * - setSelectedKeyResultId: Control prop used by KeyResults child for re-link flow
 * - setEditKeyResultsPopupOpen: Control prop used by KeyResults child to open edit modal
 * - setLoading: Control prop used by KeyResults child to show loading
 * - refreshData: Callback to refresh the search results after a mutation
 */
// src/app/common/components/search-with-tabs/all-results/all-results.tsx
import React from "react";
import { Typography } from "@mui/material";
import PeopleSearchResults from "../people/people-search-results";
import ObjectivesSearchResults from "../objectives/objective-search-results";
import InitiativesSearchResults from "../initiatives/initiative-search-results";
import KeyResultsSearchResults from "../key-results/key-results-search";
import MilestoneSearchResults from "../milestones/milestone-search-results";
import "./all-results.scss";

interface AllResultsProps {
  peopleData: any[];
  objectivesData: any[];
  initiativesData: any[];
  keyResultsData: any[];
  milestonesData: any[];
  setSelectedKeyResultId: (id: number | null) => void;
  setEditKeyResultsPopupOpen: (open: boolean) => void;
  setLoading: (loading: boolean) => void;
  refreshData: () => void;
  // Add drawer props
  isShowUserObjectiveDrawer?: boolean;
  setIsShowUserObjectiveDrawer?: (value: boolean) => void;
  selectedApprover?: any;
  setSelectedApprover?: (value: any) => void;
}

const AllResults: React.FC<AllResultsProps> = ({
  peopleData,
  objectivesData,
  initiativesData,
  keyResultsData,
  milestonesData,
  setSelectedKeyResultId,
  setEditKeyResultsPopupOpen,
  setLoading,
  refreshData,
  // Drawer props
  isShowUserObjectiveDrawer,
  setIsShowUserObjectiveDrawer,
  selectedApprover,
  setSelectedApprover,
}) => {
  return (
    <>
      {peopleData.length > 0 && (
        <>
          <Typography
            sx={{
              mt: 2,
              mb: 1,
              fontSize: "14px",
              fontFamily: "Manrope, sans-serif",
              fontWeight: 600,
              fontStyle: "semi-bold",
            }}
          >
            People
          </Typography>
          <PeopleSearchResults
            results={peopleData}
            setIsShowUserObjectiveDrawer={setIsShowUserObjectiveDrawer}
            setSelectedApprover={setSelectedApprover}
          />
        </>
      )}
      {objectivesData.length > 0 && (
        <>
          <Typography
            sx={{
              mt: 2,
              mb: 1,
              fontSize: "14px",
              fontFamily: "Manrope, sans-serif",
              fontWeight: 600,
              fontStyle: "semi-bold",
            }}
          >
            Objectives
          </Typography>
          <ObjectivesSearchResults results={objectivesData} />
        </>
      )}
      {keyResultsData.length > 0 && (
        <>
          <Typography
            sx={{
              mt: 2,
              mb: 1,
              fontSize: "14px",
              fontFamily: "Manrope, sans-serif",
              fontWeight: 600,
              fontStyle: "semi-bold",
            }}
          >
            Key Results
          </Typography>
          <KeyResultsSearchResults
            results={keyResultsData}
            refreshData={refreshData}
            setSelectedKeyResultId={setSelectedKeyResultId}
            setEditKeyResultsPopupOpen={setEditKeyResultsPopupOpen}
            setLoading={setLoading}
          />
        </>
      )}
      {initiativesData.length > 0 && (
        <>
          <Typography
            sx={{
              mt: 2,
              mb: 1,
              fontSize: "14px",
              fontFamily: "Manrope, sans-serif",
              fontWeight: 600,
              fontStyle: "semi-bold",
            }}
          >
            Initiatives
          </Typography>
          <InitiativesSearchResults
            results={initiativesData}
            refreshData={refreshData}
          />
        </>
      )}
      {milestonesData.length > 0 && (
        <>
          <Typography
            sx={{
              mt: 2,
              mb: 1,
              fontSize: "14px",
              fontFamily: "Manrope, sans-serif",
              fontWeight: 600,
              fontStyle: "semi-bold",
            }}
          >
            Milestones
          </Typography>
          <MilestoneSearchResults results={milestonesData} />
        </>
      )}
    </>
  );
};

export default AllResults;
